import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { ArrowRight, ChartLineUp, ShieldCheck, CloudArrowUp, Robot } from '@phosphor-icons/react';

const products = [
  {
    icon: <ChartLineUp size={40} weight="duotone" className="text-[#0052FF]" />,
    title: 'Cloud Cost Optimization Tool',
    description: 'Enhancing financial operations in the cloud with accuracy and efficiency. Our FinOps tool provides real-time cost analytics, reserved instance management, and AI-driven recommendations to cut cloud waste.',
    features: ['Real-time cost dashboards', 'AI-driven savings recommendations', 'Multi-cloud cost visibility', 'Budget alerting & forecasting'],
  },
  {
    icon: <ShieldCheck size={40} weight="duotone" className="text-[#0052FF]" />,
    title: 'App APM Tracker',
    description: 'Full-stack application performance monitoring with distributed tracing, real-time alerting, and intelligent anomaly detection to ensure your applications perform at their best.',
    features: ['Distributed tracing', 'Real-time performance alerts', 'Error tracking & diagnostics', 'Custom SLA monitoring'],
  },
  {
    icon: <CloudArrowUp size={40} weight="duotone" className="text-[#0052FF]" />,
    title: 'Next-Gen Instance Migration',
    description: 'Seamless migration to the latest compute instance types for better performance, cost-efficiency, and scalability. Automated assessment, planning, and execution.',
    features: ['Automated instance assessment', 'Zero-downtime migration', 'Performance benchmarking', 'Cost impact analysis'],
  },
  {
    icon: <Robot size={40} weight="duotone" className="text-[#0052FF]" />,
    title: 'Agentic AI Platform',
    description: 'Deploy autonomous AI agents that learn, adapt, and execute complex enterprise workflows. From intelligent DevSecOps to predictive operations, our AI platform transforms how you operate.',
    features: ['Autonomous AI agents', 'Predictive incident management', 'AI-powered code review', 'Intelligent automation'],
  },
];

export default function ProductsPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="products-page">
      <Header />
      <div className="pt-[72px]">
        {/* Hero */}
        <div className="bg-[#0B132B] py-20 md:py-28">
          <div className="w-full px-6 md:px-10 lg:px-16 xl:px-24">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#10B981] mb-3">Products</p>
            <h1 className="text-4xl sm:text-5xl tracking-tighter font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Our Products
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mt-4 max-w-3xl" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Enterprise-grade tools and platforms designed to optimize your cloud operations, reduce costs, and accelerate innovation.
            </p>
          </div>
        </div>

        {/* Products Grid */}
        <div className="py-20 md:py-28">
          <div className="w-full px-6 md:px-10 lg:px-16 xl:px-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {products.map((product) => (
                <div key={product.title} className="p-8 lg:p-10 border border-slate-200 hover:border-[#0052FF]/30 transition-colors duration-200 group">
                  <div className="mb-6">{product.icon}</div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-[#0B132B] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {product.title}
                  </h3>
                  <p className="text-base leading-relaxed text-[#475569] mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {product.description}
                  </p>
                  <ul className="space-y-2">
                    {product.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-base text-[#475569]">
                        <ArrowRight size={14} weight="bold" className="text-[#0052FF] flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-16 text-center">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0052FF] text-white text-base font-bold rounded-full hover:bg-[#0040CC] transition-colors duration-200"
                data-testid="products-cta-btn"
              >
                Request a Demo
                <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
